import { debounce } from '@mui/material';
import { EntityBase } from '@provision/entity-types';
import {
  MRT_RowVirtualizer,
  MRT_TableOptions,
  useMaterialReactTable,
} from 'material-react-table';
import { MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { appShortcuts } from 'src/constants/appShortcuts';
import { useRegisterShortcuts } from 'src/utils/hooks';

export const useMaterialReactTableWithShortcuts = <T extends EntityBase>(
  selectedIndex = -1,
  tableOptions: MRT_TableOptions<T>,
  onRowClick: (id: T['id'], record: T, event?: ReactMouseEvent) => void,
) => {
  const [hoveredIndex, setHoveredIndex] = useState(selectedIndex);
  const [navigatedOverKeyboard, setNavigatedOverKeyboard] = useState(false);
  const internalRowVirtualRef = useRef<MRT_RowVirtualizer>(null);
  const rowVirtualRef = tableOptions.rowVirtualizerInstanceRef ?? internalRowVirtualRef;

  const table = useMaterialReactTable({
    ...tableOptions,
    rowVirtualizerInstanceRef: rowVirtualRef,
    muiTableBodyRowProps: (props) => {
      const rowProps =
        typeof tableOptions.muiTableBodyRowProps === 'function'
          ? tableOptions.muiTableBodyRowProps(props)
          : tableOptions.muiTableBodyRowProps || {};

      return {
        ...rowProps,
        'data-row-id': props.row.id,
        onClick: (event) => {
          rowProps.onClick?.(event);

          const id = props.row.original?.id;
          if (id === 'skeleton') return;

          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          highlightRowById(props.row.id);
          setNavigatedOverKeyboard(false);
          onRowClick(id, props.row.original, event);
        },
        sx: (theme) => {
          const isFocused = props.row.index === hoveredIndex;
          const sx =
            typeof rowProps.sx === 'function' ? rowProps.sx(theme) : rowProps.sx || {};
          return {
            ...(sx as unknown as Record<string, unknown>),
            ...(!isFocused &&
              navigatedOverKeyboard && {
                '&:hover td::after': { backgroundColor: 'transparent !important' },
              }),
            '& td::after': {
              content: '" "',
              backgroundColor: isFocused
                ? theme.palette.custom.OV.custom.Table.hover
                : 'transparent',
              height: '100%',
              left: 0,
              position: 'absolute',
              top: 0,
              width: '100%',
              zIndex: -1,
            },
          };
        },
      };
    },
  });

  useEffect(() => {
    // we don't want to lose the last hover state when
    // the selected index resets to -1
    // it allows user to continue navigating where he left off
    if (selectedIndex !== -1) {
      setHoveredIndex(selectedIndex);
    }
  }, [selectedIndex]);

  const highlightRowById = (rowId: string) => {
    const rowsArr = table.getRowModel().rows;
    const idx = rowsArr.findIndex((r) => r.id === rowId);
    setHoveredIndex(idx);
    setNavigatedOverKeyboard(false);
  };

  const isKeyboardNavRef = useRef(false);
  const mouseNavResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const handleHover = (e: globalThis.MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const row = el?.closest?.('tr[data-row-id]');
      if (row) {
        highlightRowById(row.getAttribute('data-row-id')!);
      }
    };

    const debouncedHover = debounce(handleHover, 100);

    const listener = (e: globalThis.MouseEvent) => {
      if (isKeyboardNavRef.current) return;
      setNavigatedOverKeyboard(false);
      setHoveredIndex(-1);
      debouncedHover(e);
      isKeyboardNavRef.current = false;
    };

    document.addEventListener('mousemove', listener);
    return () => {
      debouncedHover.clear();
      document.removeEventListener('mousemove', listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  useRegisterShortcuts(
    useMemo(() => {
      const selectRow = (indexChange: number) => {
        // Safari considers a scroll behind the mouse cursor as a mouse move
        // so we need to put mouse listener to sleep for a short time
        // when the keyboard navigation occurs
        isKeyboardNavRef.current = true;
        clearTimeout(mouseNavResetTimeoutRef.current);
        mouseNavResetTimeoutRef.current = setTimeout(() => {
          isKeyboardNavRef.current = false;
        }, 500);

        setHoveredIndex((prevIndex) => {
          const rowsArr = table.getRowModel().rows;
          const nextIdx = Math.max(0, Math.min(prevIndex + indexChange, rowsArr.length - 1));
          const row = rowsArr[nextIdx];
          if (!row) return prevIndex;

          // adding some span to prevent selected row from being out of view
          rowVirtualRef.current?.scrollToIndex(nextIdx + indexChange + 1);
          return nextIdx;
        });

        setNavigatedOverKeyboard(true);
      };

      return [
        {
          ...appShortcuts.selectPreviousTableRow,
          callback: (e: KeyboardEvent) => {
            e.preventDefault();
            selectRow(-1);
          },
        },
        {
          ...appShortcuts.selectNextTableRow,
          callback: (e: KeyboardEvent) => {
            e.preventDefault();
            selectRow(1);
          },
        },
        {
          ...appShortcuts.openSelectedTableRow,
          callback: () => {
            const rowsArr = table.getRowModel().rows;
            const row = rowsArr[hoveredIndex];
            if (row && row.original.id !== 'skeleton') {
              onRowClick(row.original.id, row.original);
            }
          },
        },
      ];
    }, [hoveredIndex, onRowClick, table, rowVirtualRef]),
  );

  return table;
};
